/**
 * Browser-session LLM token totals (in-memory; resets on full page reload).
 * Logs each turn plus cumulative input/output/cached to the dev console.
 */

export type LlmTokenUsagePayload = {
  timestamp?: string
  model_id?: string
  input_tokens_this_turn?: number
  output_tokens_this_turn?: number
  cached_tokens_this_turn?: number
  cache_read_input_tokens_this_turn?: number
  cache_write_input_tokens_this_turn?: number
}

function n(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.floor(v))
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v)
    return Number.isFinite(x) ? Math.max(0, Math.floor(x)) : 0
  }
  return 0
}

function parsePayload(raw: unknown): LlmTokenUsagePayload | null {
  if (!raw || typeof raw !== 'object') return null
  return raw as LlmTokenUsagePayload
}

export type LlmTokenSessionTotals = {
  turnCount: number
  totalInputTokens: number
  totalOutputTokens: number
  totalCachedTokens: number
  totalCacheReadInputTokens: number
  totalCacheWriteInputTokens: number
}

export function createLlmTokenSessionLogger() {
  let turnCount = 0
  let totalInputTokens = 0
  let totalOutputTokens = 0
  let totalCachedTokens = 0
  let totalCacheReadInputTokens = 0
  let totalCacheWriteInputTokens = 0

  return {
    /**
     * Call after each /chat/command or voice session_stopped that includes `llm_token_usage`.
     */
    logTurn(source: string, usageRaw: unknown): LlmTokenSessionTotals {
      const u = parsePayload(usageRaw)
      const inp = n(u?.input_tokens_this_turn)
      const out = n(u?.output_tokens_this_turn)
      const cached = n(u?.cached_tokens_this_turn)
      const cread = n(u?.cache_read_input_tokens_this_turn)
      const cwrite = n(u?.cache_write_input_tokens_this_turn)

      turnCount += 1
      totalInputTokens += inp
      totalOutputTokens += out
      totalCachedTokens += cached
      totalCacheReadInputTokens += cread
      totalCacheWriteInputTokens += cwrite

      const totals: LlmTokenSessionTotals = {
        turnCount,
        totalInputTokens,
        totalOutputTokens,
        totalCachedTokens,
        totalCacheReadInputTokens,
        totalCacheWriteInputTokens,
      }

      console.log('[llm-tokens-session]', {
        source,
        turn: turnCount,
        this_turn: {
          input_tokens: inp,
          output_tokens: out,
          cached_tokens: cached,
          cache_read_input_tokens: cread,
          cache_write_input_tokens: cwrite,
          timestamp: u?.timestamp,
          model_id: u?.model_id,
        },
        session_aggregated: {
          total_input_tokens: totalInputTokens,
          total_output_tokens: totalOutputTokens,
          total_cached_tokens: totalCachedTokens,
          total_cache_read_input_tokens: totalCacheReadInputTokens,
          total_cache_write_input_tokens: totalCacheWriteInputTokens,
        },
      })

      // Flat one-liner so totals stay visible when object logs are collapsed in DevTools.
      console.log(
        `[llm-tokens-session-TOTALS] turns=${turnCount} total_input=${totalInputTokens} total_output=${totalOutputTokens} total_cached=${totalCachedTokens} total_cache_read_input=${totalCacheReadInputTokens} total_cache_write_input=${totalCacheWriteInputTokens} (after ${source} turn ${turnCount})`,
      )

      return totals
    },

    getTotals(): LlmTokenSessionTotals {
      return {
        turnCount,
        totalInputTokens,
        totalOutputTokens,
        totalCachedTokens,
        totalCacheReadInputTokens,
        totalCacheWriteInputTokens,
      }
    },
  }
}
